import React from 'react';
import clsx from 'clsx';
import { CheckIcon } from '@heroicons/react/24/outline';
import type { MovimentacaoEquipamentoView, TipoFluxoMovimentacao } from '../../../models';
import { ehItemSimples, fotosMinimasDoItem } from '../../../models';
import { TIPO_EQUIPAMENTO_LABELS, TIPOS_EQUIPAMENTO_COM_SENHA } from '../solicitacaoDetailsPage.definitions';

interface EquipamentosMovimentacaoListaProps {
  itens: MovimentacaoEquipamentoView[];
  tipoFluxo: TipoFluxoMovimentacao;
  /** Item aberto na gaveta (destacado na lista). */
  abertoId: string | null;
  onAbrir: (id: string) => void;
  /** "+ Adicionar equipamento" (só quem edita). */
  onAdicionar?: () => void;
}

/** O que ainda falta num item — mesmas regras de pendenciaMovimentacao, por equipamento. */
export function faltasDoItem(item: MovimentacaoEquipamentoView, tipoFluxo: TipoFluxoMovimentacao): string[] {
  const faltas: string[] = [];
  const periferico = item.tipoEquipamento === 'periferico';
  if (!item.tipoEquipamento) faltas.push('tipo');
  if (!periferico && !item.patrimonio) faltas.push('patrimônio');
  if (!periferico && !ehItemSimples(item.tipoEquipamento) && !item.nSerie) faltas.push('nº de série');
  if (!item.condicao) faltas.push('condição');
  if (!((item.acessorios && item.acessorios.length > 0) || item.acessoriosOutros)) faltas.push('acessórios');
  if (
    tipoFluxo === 'envio' &&
    item.tipoEquipamento &&
    TIPOS_EQUIPAMENTO_COM_SENHA.includes(item.tipoEquipamento) &&
    !item.homologado &&
    !item.senha
  ) {
    faltas.push('senha');
  }
  const fotos = (item.fotos ?? []).length;
  const minimo = fotosMinimasDoItem(item.tipoEquipamento);
  if (fotos < minimo) faltas.push(`fotos ${fotos}/${minimo}`);
  if (!item.glpiAtualizado) faltas.push(tipoFluxo === 'envio' ? 'GLPI' : 'GLPI (desvincular)');
  return faltas;
}

/** Equipamentos da Movimentação em linhas curtas (o que é + o que falta); clicar abre o
 * editor do item na gaveta ao lado (MovimentacaoCard) — um por vez. */
export default function EquipamentosMovimentacaoLista({
  itens,
  tipoFluxo,
  abertoId,
  onAbrir,
  onAdicionar,
}: EquipamentosMovimentacaoListaProps) {
  return (
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Equipamentos</p>
      {itens.map((item) => {
        const faltas = faltasDoItem(item, tipoFluxo);
        const selecionado = item.id === abertoId;
        const tipo = item.tipoEquipamento ? TIPO_EQUIPAMENTO_LABELS[item.tipoEquipamento] : 'Equipamento a definir';
        const identificacao = [item.patrimonio, item.modelo ?? item.descricaoLivre].filter(Boolean).join(' · ');
        return (
          <button
            key={item.id}
            type="button"
            aria-pressed={selecionado}
            onClick={() => onAbrir(item.id)}
            className={clsx(
              'flex w-full flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-lg px-3 py-2.5 text-left text-sm',
              selecionado ? 'border-2 border-primary-500 bg-primary-50' : 'border border-gray-200 bg-white hover:bg-gray-50'
            )}
          >
            <span className="min-w-0">
              <span className="font-semibold text-gray-900">{tipo}</span>
              {identificacao && <span className="text-gray-600"> · {identificacao}</span>}
            </span>
            {faltas.length === 0 ? (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700">
                <CheckIcon className="h-4 w-4" aria-hidden="true" />
                Completo
              </span>
            ) : (
              <span className="text-xs font-medium text-amber-800">
                Falta: {faltas.slice(0, 3).join(', ')}
                {faltas.length > 3 ? ` +${faltas.length - 3}` : ''}
              </span>
            )}
          </button>
        );
      })}
      {onAdicionar && (
        <button
          type="button"
          onClick={onAdicionar}
          className="rounded-lg border border-dashed border-primary-300 bg-white px-3 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-50"
        >
          + Adicionar equipamento
        </button>
      )}
    </div>
  );
}
